import type {
  CampaignActionResponseDto,
  CampaignListItemDto,
  CampaignStatusResponseDto,
  CreateCampaignDto,
  PaginatedCampaignsDto,
  PreviewCampaignResponseDto,
  PresignResponseDto,
  SaveDraftDto,
  StoreEmailDto,
  StoreListResponseDto,
} from "../types/campaign";

function getApiBase(): string {
  const base =
    window.__ENV__?.REACT_APP_EMAIL_API_URL ||
    process.env.REACT_APP_EMAIL_API_URL ||
    "";

  return base.replace(/\/$/, "");
}

function getAuthToken(): string {
  return (
    window.__ENV__?.REACT_APP_AUTH_TOKEN ||
    process.env.REACT_APP_AUTH_TOKEN ||
    ""
  );
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getAuthToken();
  const isFormData = init.body instanceof FormData;
  const headers = new Headers(init.headers);

  if (!isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const res = await fetch(`${getApiBase()}${path}`, { ...init, headers });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}${text ? `: ${text}` : ""}`);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export async function fetchStores(storeNumbers?: string[]): Promise<StoreEmailDto[]> {
  const qs = storeNumbers?.length ? `?storeNumbers=${storeNumbers.join(",")}` : "";
  return apiFetch<StoreEmailDto[]>(`/api/email/stores${qs}`);
}

export async function fetchStoreGroups(storeNumbers?: string[]): Promise<StoreListResponseDto> {
  const qs = storeNumbers?.length ? `?storeNumbers=${storeNumbers.join(",")}` : "";
  return apiFetch<StoreListResponseDto>(`/api/email/stores/groups${qs}`);
}

export async function createCampaign(
  dto: CreateCampaignDto,
): Promise<CampaignActionResponseDto> {
  return apiFetch<CampaignActionResponseDto>("/api/email/campaigns", {
    method: "POST",
    body: JSON.stringify(dto),
  });
}

export async function listCampaigns(params?: {
  pageSize?: number;
  continuationToken?: string;
  isDraft?: boolean;
}): Promise<PaginatedCampaignsDto> {
  const searchParams = new URLSearchParams();

  if (params?.pageSize) searchParams.set("pageSize", String(params.pageSize));
  if (params?.continuationToken) {
    searchParams.set("continuationToken", params.continuationToken);
  }
  if (params?.isDraft !== undefined) {
    searchParams.set("isDraft", String(params.isDraft));
  }

  const qs = searchParams.toString() ? `?${searchParams.toString()}` : "";
  return apiFetch<PaginatedCampaignsDto>(`/api/email/campaigns${qs}`);
}

export async function getCampaign(campaignId: string): Promise<CampaignStatusResponseDto> {
  return apiFetch<CampaignStatusResponseDto>(`/api/email/campaigns/${campaignId}`);
}

export async function findCampaignListItem(
  campaignId: string,
): Promise<CampaignListItemDto | null> {
  const drafts = await listDrafts();
  const draft = drafts.find((item) => item.campaignId === campaignId);

  if (draft) {
    return draft;
  }

  let continuationToken: string | undefined;

  do {
    const page = await listCampaigns({ pageSize: 100, continuationToken, isDraft: false });
    const item = page.items.find((entry) => entry.campaignId === campaignId);

    if (item) {
      return item;
    }

    continuationToken = page.continuationToken;
  } while (continuationToken);

  return null;
}

export async function retryCampaign(
  campaignId: string,
  payload: { subject: string; body: string },
): Promise<{ retried: number }> {
  return apiFetch<{ retried: number }>(`/api/email/campaigns/${campaignId}/retry`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function saveDraft(dto: SaveDraftDto): Promise<CampaignActionResponseDto> {
  return apiFetch<CampaignActionResponseDto>("/api/email/drafts", {
    method: "POST",
    body: JSON.stringify({ ...dto, isDraft: true }),
  });
}

export async function updateDraft(
  campaignId: string,
  dto: SaveDraftDto,
): Promise<CampaignActionResponseDto> {
  return apiFetch<CampaignActionResponseDto>(`/api/email/drafts/${campaignId}`, {
    method: "PUT",
    body: JSON.stringify(dto),
  });
}

export async function listDrafts(): Promise<CampaignListItemDto[]> {
  return apiFetch<CampaignListItemDto[]>("/api/email/drafts");
}

export async function sendDraft(campaignId: string): Promise<CampaignActionResponseDto> {
  return apiFetch<CampaignActionResponseDto>(`/api/email/drafts/${campaignId}/send`, {
    method: "POST",
  });
}

export async function previewCampaign(
  campaign: CreateCampaignDto,
  previewStoreNumber: string,
): Promise<PreviewCampaignResponseDto> {
  return apiFetch<PreviewCampaignResponseDto>("/api/email/preview", {
    method: "POST",
    body: JSON.stringify({ campaign, preview: { previewStoreNumber } }),
  });
}

export async function presignAttachment(
  storeNumber: string,
  filename: string,
): Promise<PresignResponseDto> {
  return apiFetch<PresignResponseDto>("/api/email/attachments/presign", {
    method: "POST",
    body: JSON.stringify({ storeNumber, filename }),
  });
}

export async function uploadAttachmentToBlob(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });

  if (!res.ok) {
    throw new Error(`Blob upload failed: ${res.status}`);
  }
}

export async function uploadStoreAttachment(
  storeNumber: string,
  file: File,
): Promise<string> {
  const { uploadUrl, blobPath } = await presignAttachment(storeNumber, file.name);
  await uploadAttachmentToBlob(uploadUrl, file);
  return blobPath;
}
